/// <reference types="@types/google.maps" />

import { use, Suspense } from "react"
import { Button } from "./button"
import { Control, FieldValues, Path, useController } from "react-hook-form"
import { Pencil } from "lucide-react"

const placesLibraryPromise =
  window.google &&
  (google.maps.importLibrary("places") as Promise<google.maps.PlacesLibrary>)

export type PlaceAddress = {
  addressComponents: Array<{
    longText: string | null
    shortText: string | null
    types: Array<string>
  }>
  formattedAddress: string | null
}

export function ControlledPlaceInput<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  name,
  control,
  rules,
  ...props
}: {
  name?: TName
  control?: Control<TFieldValues>
  rules?: object
} & Omit<React.ComponentProps<typeof PlaceInput>, "value" | "onChange">) {
  const { field } = useController({ name: name!, control, rules })

  return (
    <PlaceInput
      {...props}
      value={field.value}
      onChange={(value) => field.onChange(value)}
    />
  )
}

export function PlaceInput({
  value,
  ...props
}: {
  value: PlaceAddress | null
} & React.ComponentProps<typeof PlaceAutocomplete>) {
  return (
    <div className="flex min-h-12 flex-row items-center justify-between">
      {value ? (
        <>
          <div>{value.formattedAddress ?? "No address"}</div>
          <Button onClick={() => props.onChange?.(null)}>
            <Pencil />
            Edit
          </Button>
        </>
      ) : (
        <Suspense fallback={null}>
          <div className="w-full">
            <PlaceAutocomplete {...props} />
          </div>
        </Suspense>
      )}
    </div>
  )
}

function PlaceAutocomplete({
  onChange,
}: {
  onChange?: (placesAddress: PlaceAddress | null) => void
}) {
  if (!placesLibraryPromise) {
    return null
  }

  // @ts-expect-error types to be defined in a later release of @types/google.maps
  const { PlaceAutocompleteElement } = use(placesLibraryPromise)

  const placeAutocomplete = new PlaceAutocompleteElement()
  placeAutocomplete.addEventListener(
    "gmp-select",
    async ({
      placePrediction,
    }: {
      placePrediction: google.maps.places.PlacePrediction
    }) => {
      const place = placePrediction.toPlace()
      await place.fetchFields({
        fields: ["addressComponents", "formattedAddress"],
      })
      onChange?.({
        addressComponents: place.addressComponents!.map(
          ({ longText, shortText, types }) => ({
            longText,
            shortText,
            types,
          }),
        ),
        formattedAddress: place.formattedAddress!,
      })
    },
  )

  return (
    <div
      ref={(node) => {
        if (node) {
          node.appendChild(placeAutocomplete)
        }

        return () => placeAutocomplete.remove()
      }}
    ></div>
  )
}
